import type { Lang } from '../../i18n';

/**
 * The player guide (/guide). Content lives here rather than in the UI dictionary because it
 * is long prose; each section has a stable id so in-app "?" links can open it directly.
 * Numbers mirror packages/game-engine/src/rules.ts — update both together.
 */

export type GuideImage = { src: string; alt: string };
export interface GuideStep {
  text: string;
  /** Screenshot under /guide-shots/<name>.jpg; a drawn placeholder shows until the file exists. */
  img?: string;
}
export interface GuideBlock {
  title?: string;
  /** Shown only on this platform's tab (install section). */
  platform?: 'ios' | 'android';
  /** Anchor of its own (e.g. #vi-tri inside "Account & permissions"). */
  id?: string;
  text?: string;
  steps?: GuideStep[];
  bullets?: string[];
  tip?: string;
}
export interface GuideSection {
  id: string;
  icon: string;
  title: string;
  blocks: GuideBlock[];
  faq?: Array<{ q: string; a: string }>;
}
export interface GuideCopy {
  title: string;
  subtitle: string;
  contents: string;
  iphone: string;
  android: string;
  back: string;
  signIn: string;
  screenshotSoon: string;
  sections: GuideSection[];
}

/** Screenshots that exist in apps/web/public/guide-shots (the others show a placeholder). */
export const GUIDE_IMAGES = new Set<string>([
  'ios-zalo',
  'ios-share',
  'ios-add-home',
  'ios-add-confirm',
  'ios-icon',
  'ios-location-prompt',
  'ios-location-settings',
  'ios-notifications',
  'ios-notification-types',
]);

const vi: GuideCopy = {
  title: 'Hướng dẫn Runner Wars',
  subtitle: 'Cài game, cách chơi và câu hỏi thường gặp.',
  contents: 'Nội dung',
  iphone: 'iPhone',
  android: 'Android',
  back: '← Quay lại',
  signIn: 'Đăng nhập / tạo tài khoản',
  screenshotSoon: 'Hình minh hoạ sẽ được cập nhật',
  sections: [
    {
      id: 'cai-dat',
      icon: '📲',
      title: 'Cài game',
      blocks: [
        {
          text: 'Runner Wars là web app: không có trên App Store hay CH Play. Bạn cài thẳng từ trình duyệt, sau đó game mở toàn màn hình từ biểu tượng trên điện thoại như một app bình thường và tự cập nhật.',
          tip: 'Đang mở link trong Zalo hoặc Facebook? Phải chuyển sang trình duyệt trước, vì không cài được ngay trong Zalo.',
          steps: [{ text: 'Trong Zalo: bấm ⋯ ở góc trên bên phải → “Mở bằng Safari” (Android: “Mở bằng trình duyệt”).', img: 'ios-zalo' }],
        },
        {
          platform: 'ios',
          steps: [
            { text: 'Mở www.runnerwars.com bằng Safari, bấm nút ⋯ ở thanh dưới → “Chia sẻ” (Share). Máy đời cũ: bấm thẳng nút Chia sẻ (ô vuông có mũi tên).', img: 'ios-share' },
            { text: 'Trong bảng Chia sẻ, chọn “Thêm vào MH chính” (Add to Home Screen).', img: 'ios-add-home' },
            { text: 'Giữ “Mở dưới dạng ứng dụng web” (Open as Web App) đang bật, rồi bấm “Thêm” (Add).', img: 'ios-add-confirm' },
            { text: 'Mở Runner Wars từ biểu tượng mới trên màn hình chính (không mở trong Safari nữa).', img: 'ios-icon' },
          ],
          tip: 'Thông báo trên iPhone chỉ hoạt động khi mở game từ biểu tượng trên màn hình chính.',
        },
        {
          platform: 'android',
          steps: [
            { text: 'Mở www.runnerwars.com bằng Chrome.' },
            { text: 'Bấm “Cài đặt” trên thẻ “Cài Runner Wars” ở Trang chủ, hoặc mở menu ⋮ → “Cài đặt ứng dụng” (hoặc “Thêm vào màn hình chính”).' },
            { text: 'Xác nhận “Cài đặt”, rồi mở Runner Wars từ biểu tượng mới.' },
          ],
        },
        { tip: 'Game tự cập nhật. Nếu vẫn thấy bản cũ, hãy đóng hẳn game rồi mở lại.' },
      ],
    },
    {
      id: 'dang-nhap',
      icon: '🔑',
      title: 'Tài khoản và quyền truy cập',
      blocks: [
        {
          text: 'Tạo tài khoản bằng email (hoặc đăng nhập bằng Strava nếu có nút). Đổi ngôn ngữ trong Hồ sơ → Ngôn ngữ.',
        },
        {
          id: 'vi-tri',
          title: 'Cho phép vị trí (bắt buộc để chạy)',
          platform: 'ios',
          steps: [
            { text: 'Lần đầu bấm Chạy, điện thoại hỏi quyền vị trí: chọn “Cho phép” (Allow).', img: 'ios-location-prompt' },
            {
              text: 'Nếu lỡ chặn: Cài đặt → Quyền riêng tư & Bảo mật → Dịch vụ định vị → Trang web Safari → chọn “Khi dùng ứng dụng” và bật “Vị trí chính xác”. Sau đó mở lại game.',
              img: 'ios-location-settings',
            },
          ],
        },
        {
          title: 'Cho phép vị trí (bắt buộc để chạy)',
          platform: 'android',
          steps: [
            { text: 'Lần đầu bấm Chạy, chọn “Cho phép” (và “Chính xác” nếu được hỏi).' },
            { text: 'Nếu lỡ chặn: Cài đặt điện thoại → Ứng dụng → Chrome (hoặc Runner Wars) → Quyền → Vị trí → Cho phép, bật “Dùng vị trí chính xác”. Sau đó mở lại game.' },
          ],
        },
        {
          title: 'Bật thông báo',
          steps: [
            { text: 'Hồ sơ → Thông báo → Bật, rồi chọn “Cho phép” (Allow) khi điện thoại hỏi.', img: 'ios-notifications' },
            { text: 'Chọn loại thông báo muốn nhận: bị cướp ô, kudos và bình luận, sự kiện, câu lạc bộ, buổi chạy từ Strava.', img: 'ios-notification-types' },
          ],
        },
      ],
    },
    {
      id: 'chay',
      icon: '🏃',
      title: 'Chạy trong game',
      blocks: [
        {
          steps: [
            { text: 'Bấm nút CHẠY ở giữa thanh dưới, chờ GPS báo sẵn sàng rồi bấm Bắt đầu.' },
            { text: 'Giữ game mở trên màn hình khi chạy: game giữ màn hình sáng. iPhone dừng GPS khi khoá màn hình.' },
            { text: 'Tạm dừng / tiếp tục khi cần. Xong thì bấm Kết thúc → máy chủ kiểm tra buổi chạy và tính quãng đường, ô chiếm được, XP.' },
          ],
          bullets: [
            'Mất mạng vẫn chạy bình thường: buổi chạy lưu trên máy và tự gửi lên khi có mạng.',
            'Máy chủ mới là nơi quyết định: đi xe, GPS nhảy bất thường hoặc tốc độ quá 30 km/h sẽ bị loại.',
            'Có thể chạy bằng đồng hồ thay vì điện thoại: xem phần “Dùng đồng hồ”.',
          ],
        },
      ],
    },
    {
      id: 'chiem-o',
      icon: '⬢',
      title: 'Chiếm ô đất',
      blocks: [
        {
          bullets: [
            'Bản đồ game là phần đất liền của Đà Nẵng (gồm cả Quảng Nam cũ), chia thành các ô lục giác rộng khoảng 200 m. Ngoài biển và trên các đảo không có ô.',
            'Chạy ít nhất 30 m bên trong một ô (với GPS tốt) là chiếm được ô đó.',
            'Ô đang của người khác thì bị bạn cướp; ô của bạn cũng có thể bị cướp lại.',
            'Lần chạy qua sau cùng sẽ giữ ô. Buổi chạy đồng bộ muộn không cướp được ô mà người khác đã chiếm sau giờ chạy đó.',
            'Bản đồ: ô của bạn màu xanh chanh, ô của đối thủ theo màu của họ, ô trống không tô.',
            'Đối thủ: những người cướp ô của bạn hoặc bị bạn cướp, xem ở Hồ sơ → Đối thủ.',
          ],
        },
      ],
    },
    {
      id: 'dong-ho',
      icon: '⌚',
      title: 'Dùng đồng hồ (COROS, Garmin, Suunto, Apple Watch)',
      blocks: [
        {
          title: 'Qua Strava (tự động)',
          text: 'Hồ sơ → Kết nối → nút “Connect with Strava”. Từ đó mỗi buổi chạy đồng hồ gửi lên Strava sẽ tự vào game. Lưu ý: số người kết nối Strava đang bị Strava giới hạn, khi hết chỗ hãy dùng cách tải file bên dưới.',
        },
        {
          title: 'Tải file FIT / GPX / TCX',
          text: 'Hồ sơ → Kết nối → “Tải file buổi chạy” → Tải lên, chọn file của buổi chạy. Chỉ nhận buổi chạy trong 7 ngày gần nhất và sau khi bạn tạo tài khoản.',
        },
        {
          title: 'COROS (ngay trên điện thoại)',
          steps: [
            { text: 'App COROS → mở buổi chạy → bấm ⋯.', img: 'coros-menu' },
            { text: 'Chọn Export data → FIT.', img: 'coros-export' },
            { text: 'Lưu vào Tệp (Files), rồi vào Runner Wars → Hồ sơ → Kết nối → Tải lên và chọn file vừa lưu.', img: 'coros-save' },
          ],
        },
        {
          title: 'Garmin',
          steps: [{ text: 'Garmin Connect bản web (connect.garmin.com) → mở buổi chạy → ⚙ → Export Original (FIT), hoặc Export to GPX.', img: 'garmin-export' }],
        },
        {
          title: 'Suunto, Apple Watch',
          bullets: [
            'Suunto: app Suunto → buổi chạy → ⋯ → Export GPX.',
            'Apple Watch: dùng app như HealthFit hoặc RunGap để xuất buổi chạy ra FIT/GPX (Apple Health không có kết nối web).',
          ],
        },
        {
          tip: 'Mỗi buổi chạy chỉ được tính một lần, dù ghi bằng app, Strava hay file. Muốn nhập lại một buổi, mở buổi chạy đó → “Xoá buổi chạy này” rồi nhập lại.',
        },
      ],
    },
    {
      id: 'xp',
      icon: '⭐',
      title: 'XP, cấp độ và bảng xếp hạng',
      blocks: [
        {
          bullets: [
            '10 XP cho mỗi km.',
            '100 XP cho mỗi ô chiếm được.',
            '+50 XP cho buổi chạy đầu tiên trong ngày, +25 XP khi hoàn thành buổi chạy (ít nhất 200 m và 1 phút).',
            'Lên cấp: cấp L cần 250 × L XP. Chạy mỗi ngày để giữ chuỗi ngày chạy.',
            'Bảng xếp hạng theo XP, quãng đường và số ô đang giữ.',
          ],
        },
      ],
    },
    {
      id: 'su-kien',
      icon: '🏆',
      title: 'Câu lạc bộ, sự kiện và đội',
      blocks: [
        {
          title: 'Câu lạc bộ',
          text: 'Mỗi runner thuộc tối đa một câu lạc bộ. Tham gia qua link mời, hoặc vào Trang chủ → Câu lạc bộ để tìm và xin vào.',
        },
        {
          title: 'Sự kiện',
          bullets: [
            'Ai cũng tạo được sự kiện (Xếp hạng → Sự kiện) và mời người khác bằng link.',
            'Mỗi sự kiện có bản đồ riêng, không ảnh hưởng bản đồ chung.',
            'Chỉ tính các buổi chạy bắt đầu sau khi bạn tham gia và đạt luật của sự kiện (quãng đường, pace mỗi buổi…). Mở buổi chạy để xem nó có được tính không và vì sao.',
            'Để có hạng khi kết thúc, phải đủ điều kiện (ví dụ số buổi tối thiểu, tổng km). Ai chưa đủ sẽ thấy phần còn thiếu ngay dưới tên.',
            'Kết quả được chốt khi sự kiện kết thúc.',
          ],
        },
        {
          title: 'Đội',
          bullets: [
            'Sự kiện có đội thì chọn đội khi tham gia; đổi đội được trước giờ bắt đầu.',
            'Đồng đội không cướp ô của nhau, cùng nhau mở rộng lãnh thổ của đội.',
            'Ban tổ chức có thể chuyển runner sang đội khác để cân bằng; các ô người đó giữ đi theo họ.',
          ],
        },
      ],
    },
    {
      id: 'hoi-dap',
      icon: '❓',
      title: 'Câu hỏi thường gặp',
      blocks: [],
      faq: [
        { q: 'Buổi chạy bị “Bị loại”?', a: 'Mở buổi chạy để xem ghi chú kiểm tra. Thường do đi xe, GPS nhảy bất thường hoặc tín hiệu quá kém.' },
        { q: 'Buổi chạy “Đang xử lý” mãi?', a: 'Đó là buổi bắt đầu trong game nhưng chưa bao giờ kết thúc. Mở nó ra → “Xoá buổi chạy này”, vì nó còn chặn việc tải lên buổi chạy cùng giờ.' },
        { q: 'Buổi chạy không được tính trong sự kiện?', a: 'Mở buổi chạy, phần Sự kiện ghi rõ lý do (chạy trước khi tham gia, ngắn hơn quãng đường tối thiểu, pace…).' },
        { q: 'Chạy qua mà không chiếm được ô?', a: 'Cần chạy ít nhất 30 m bên trong ô với GPS tốt. Nếu buổi chạy được đồng bộ muộn, ô mà người khác chiếm sau giờ đó vẫn thuộc về họ.' },
        { q: 'Tải file lên báo “đã có buổi chạy vào thời gian này”?', a: 'Game mở buổi chạy đang trùng giờ: xoá nó đi rồi tải file lên lại.' },
        { q: 'Xoá tài khoản thế nào?', a: 'Hồ sơ → cuối trang → “Xoá tài khoản”. Không thể hoàn tác.' },
      ],
    },
  ],
};

const en: GuideCopy = {
  title: 'Runner Wars guide',
  subtitle: 'Install the game, how to play, and common questions.',
  contents: 'Contents',
  iphone: 'iPhone',
  android: 'Android',
  back: '← Back',
  signIn: 'Sign in / create an account',
  screenshotSoon: 'Screenshot coming soon',
  sections: [
    {
      id: 'cai-dat',
      icon: '📲',
      title: 'Install the game',
      blocks: [
        {
          text: 'Runner Wars is a web app: it is not on the App Store or Google Play. You install it from the browser; it then opens full screen from its own icon like any app and updates itself.',
          tip: 'Opened the link inside Zalo or Facebook? Switch to the browser first: it cannot be installed inside Zalo.',
          steps: [{ text: 'In Zalo: tap ⋯ at the top right → “Open in Safari” (Android: “Open in browser”).', img: 'ios-zalo' }],
        },
        {
          platform: 'ios',
          steps: [
            { text: 'Open www.runnerwars.com in Safari, tap ⋯ in the bottom bar → “Share”. Older iPhones: tap the Share button (square with an arrow) directly.', img: 'ios-share' },
            { text: 'In the Share sheet, choose “Add to Home Screen”.', img: 'ios-add-home' },
            { text: 'Keep “Open as Web App” on, then tap “Add”.', img: 'ios-add-confirm' },
            { text: 'Open Runner Wars from the new home-screen icon (not in Safari any more).', img: 'ios-icon' },
          ],
          tip: 'On iPhone, notifications only work when the game is opened from its home-screen icon.',
        },
        {
          platform: 'android',
          steps: [
            { text: 'Open www.runnerwars.com in Chrome.' },
            { text: 'Tap “Install” on the “Install Runner Wars” card on Home, or open the ⋮ menu → “Install app” (or “Add to Home screen”).' },
            { text: 'Confirm “Install”, then open Runner Wars from its new icon.' },
          ],
        },
        { tip: 'The game updates itself. If you still see an old version, close it completely and open it again.' },
      ],
    },
    {
      id: 'dang-nhap',
      icon: '🔑',
      title: 'Account and permissions',
      blocks: [
        { text: 'Create an account with your email (or sign in with Strava when the button is there). Change the language in Profile → Language.' },
        {
          id: 'vi-tri',
          title: 'Allow location (needed to run)',
          platform: 'ios',
          steps: [
            { text: 'The first time you tap Run, the phone asks for your location: choose “Allow”.', img: 'ios-location-prompt' },
            {
              text: 'Blocked it by mistake? Settings → Privacy & Security → Location Services → Safari Websites → “While Using the App”, and turn on “Precise Location”. Then reopen the game.',
              img: 'ios-location-settings',
            },
          ],
        },
        {
          title: 'Allow location (needed to run)',
          platform: 'android',
          steps: [
            { text: 'The first time you tap Run, choose “Allow” (and “Precise” if asked).' },
            { text: 'Blocked it by mistake? Phone Settings → Apps → Chrome (or Runner Wars) → Permissions → Location → Allow, with “Use precise location” on. Then reopen the game.' },
          ],
        },
        {
          title: 'Turn on notifications',
          steps: [
            { text: 'Profile → Notifications → Turn on, then “Allow” when the phone asks.', img: 'ios-notifications' },
            { text: 'Pick what you want to hear about: lost sectors, kudos and comments, events, clubs, runs from Strava.', img: 'ios-notification-types' },
          ],
        },
      ],
    },
    {
      id: 'chay',
      icon: '🏃',
      title: 'Running in the game',
      blocks: [
        {
          steps: [
            { text: 'Tap RUN in the middle of the bottom bar, wait for the GPS to be ready, then tap Start.' },
            { text: 'Keep the game open on screen while running: it keeps the screen on. iPhone stops GPS when the screen locks.' },
            { text: 'Pause / resume as needed. When done, tap Finish → the server checks the run and works out distance, sectors and XP.' },
          ],
          bullets: [
            'No signal? Keep running: the run is saved on the phone and uploads itself later.',
            'The server decides: vehicles, abnormal GPS jumps or speeds over 30 km/h are rejected.',
            'You can run with a watch instead of the phone: see “Using a watch”.',
          ],
        },
      ],
    },
    {
      id: 'chiem-o',
      icon: '⬢',
      title: 'Capturing sectors',
      blocks: [
        {
          bullets: [
            'The game map is the mainland of Đà Nẵng (including former Quảng Nam), split into hexagonal sectors about 200 m across. There are no sectors at sea or on islands.',
            'Run at least 30 m inside a sector (with good GPS) to capture it.',
            'A sector held by someone else becomes yours; yours can be taken back too.',
            'The latest run through a sector holds it. A run synced late cannot take a sector someone captured after it.',
            'On the map your sectors are lime, rivals’ are in their colour, free ones are empty.',
            'Rivals: people who took your sectors or lost theirs to you, in Profile → Rivals.',
          ],
        },
      ],
    },
    {
      id: 'dong-ho',
      icon: '⌚',
      title: 'Using a watch (COROS, Garmin, Suunto, Apple Watch)',
      blocks: [
        {
          title: 'Through Strava (automatic)',
          text: 'Profile → Connections → “Connect with Strava”. Every run your watch sends to Strava then arrives in the game. Note: Strava currently limits how many runners can connect; when it is full, upload files as below.',
        },
        {
          title: 'Upload a FIT / GPX / TCX file',
          text: 'Profile → Connections → “Upload a run file” → Upload, and pick the run’s file. Only runs from the last 7 days, after you created your account.',
        },
        {
          title: 'COROS (right on the phone)',
          steps: [
            { text: 'COROS app → open the run → tap ⋯.', img: 'coros-menu' },
            { text: 'Choose Export data → FIT.', img: 'coros-export' },
            { text: 'Save to Files, then in Runner Wars → Profile → Connections → Upload, pick it.', img: 'coros-save' },
          ],
        },
        {
          title: 'Garmin',
          steps: [{ text: 'Garmin Connect on the web (connect.garmin.com) → open the run → ⚙ → Export Original (FIT), or Export to GPX.', img: 'garmin-export' }],
        },
        {
          title: 'Suunto, Apple Watch',
          bullets: [
            'Suunto: Suunto app → run → ⋯ → Export GPX.',
            'Apple Watch: use an app like HealthFit or RunGap to export the workout as FIT/GPX (Apple Health has no web access).',
          ],
        },
        { tip: 'Each run counts once, whether recorded in the app, through Strava or as a file. To import a run again, open it → “Delete this run”, then import it again.' },
      ],
    },
    {
      id: 'xp',
      icon: '⭐',
      title: 'XP, levels and rankings',
      blocks: [
        {
          bullets: [
            '10 XP per km.',
            '100 XP per sector captured.',
            '+50 XP for the first run of the day, +25 XP for a completed run (at least 200 m and 1 minute).',
            'Levels: level L needs 250 × L XP. Run every day to keep your streak.',
            'Rankings by XP, distance and sectors held.',
          ],
        },
      ],
    },
    {
      id: 'su-kien',
      icon: '🏆',
      title: 'Clubs, events and teams',
      blocks: [
        { title: 'Clubs', text: 'A runner belongs to at most one club. Join through an invite link, or go to Home → Clubs to find one and ask to join.' },
        {
          title: 'Events',
          bullets: [
            'Anyone can create an event (Ranks → Events) and invite others with a link.',
            'Each event has its own map, separate from the main map.',
            'Only runs started after you joined that meet the event’s rules (distance, pace per run…) count. Open a run to see whether it counted and why.',
            'To be ranked at the end you must qualify (e.g. a minimum number of runs or total km). Anyone not yet qualified sees what is missing under their name.',
            'Results are frozen when the event ends.',
          ],
        },
        {
          title: 'Teams',
          bullets: [
            'In team events you pick a team when joining; you can switch until the start.',
            'Teammates never take each other’s sectors: you grow the team’s territory together.',
            'Organisers can move a runner to another team to balance them; the sectors they hold move with them.',
          ],
        },
      ],
    },
    {
      id: 'hoi-dap',
      icon: '❓',
      title: 'Common questions',
      blocks: [],
      faq: [
        { q: 'My run says “Rejected”?', a: 'Open it to see the validation notes. Usually a vehicle, abnormal GPS jumps or very poor signal.' },
        { q: 'A run stays “Pending” forever?', a: 'It was started in the game but never finished. Open it → “Delete this run”: it also blocks uploading a run at the same time.' },
        { q: 'A run did not count for an event?', a: 'Open the run: its Events part gives the reason (started before you joined, shorter than the minimum, pace…).' },
        { q: 'I ran through a sector but did not capture it?', a: 'You need at least 30 m inside it with good GPS. If the run synced late, a sector someone captured after it stays theirs.' },
        { q: 'Upload says “You already have a run at this time”?', a: 'The game opens the overlapping run: delete it, then upload the file again.' },
        { q: 'How do I delete my account?', a: 'Profile → bottom of the page → “Delete account”. This cannot be undone.' },
      ],
    },
  ],
};

export const GUIDE: Record<Lang, GuideCopy> = { vi, en };
